import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ShortcutButton from './ShortcutButton';
import { useTheme } from '../../middleware/ThemeContext';
import { Theme } from '../../utils/themes';

type DropdownItem = {
    label: string;
    onClick?: () => void;
    icon?: React.ReactNode;                // optional icon for menu item
    iconPosition?: 'left' | 'right';       // optional icon position
    isSelected?: boolean;
    isSeparator?: boolean;
};

type DropdownButtonProps = {
    buttonLabel: string;
    items: DropdownItem[];
    position?: 'top' | 'bottom';
    align?: 'left' | 'right';
    icon?: React.ReactNode;                 // icon for toggle button
    iconPosition?: 'left' | 'right';        // position of toggle icon
    removeborder?: boolean;
    fontSize?: string;
    fontWeight?: number;
    menuFontWeight?: number;

};

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Menu = styled.div<{
    $position: 'top' | 'bottom';
    $align: 'left' | 'right';
    theme: Theme;
    removeborder?: boolean;
}>`
  position: absolute;
  ${(props) => (props.$position === 'bottom' ? 'top: 100%;' : 'bottom: 100%;')}
  ${(props) => (props.$align === 'right' ? 'right: 0;' : 'left: 0;')}
  z-index: 1000;
  display: block;
  width: max-content;
  max-width: 200px;
  padding: 10px 0px;
  font-size: 1rem;
  font-weight: 500;
  font-family: var(--font-raleway);
  
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  border: ${({ removeborder }) => removeborder ? 'none' : `1px solid rgba(0, 0, 0, 0.15)`};
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  margin-top:10px;
`;

const MenuItem = styled.button<{ theme: Theme, menuFontWeight?: number, isSelected?: boolean }>`
  display: flex;
    align-items: center;
  width: 100%;
    font-weight: ${({ isSelected, menuFontWeight }) => (isSelected ? 600 : (menuFontWeight || 400))};
  gap: 8px;
  color:  ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  padding:5px 15px;
  font-size: 14px;
  text-align: left;
    background-color: ${({ isSelected, theme }) =>
        isSelected ? theme.CorporateTheme.colors.headerBG : 'transparent'};
  border: 0;

  &:hover {
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
  }
    &.icon-right {
    flex-direction: row-reverse;
  }
`;

const MenuSeparator = styled.div<{ theme: Theme }>`
    height: 1px;
    background-color: ${({ theme }) => theme.CreateFormTheme.colors.border};
    margin: 6px 0;
`;

const DropdownButton: React.FC<DropdownButtonProps> = ({
    buttonLabel,
    items,
    position,
    align = 'left',
    icon,
    iconPosition,
    removeborder,
    fontSize,
    fontWeight,
    menuFontWeight
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [autoPosition, setAutoPosition] = useState<'top' | 'bottom'>('bottom');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();
    const toggleDropdown = () => {
        if (!position && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow < 200 && spaceAbove > spaceBelow) {
                setAutoPosition('top');
            } else {
                setAutoPosition('bottom');
            }
        }

        setIsOpen((prev) => !prev);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const finalPosition = position ?? autoPosition;

    return (
        <Wrapper ref={wrapperRef}>
            <ShortcutButton icon={icon} label={buttonLabel} onClick={toggleDropdown} iconPostions={iconPosition} fontSize={fontSize} fontWeight={fontWeight} />
            {isOpen && (
                <Menu $position={finalPosition} $align={align} theme={theme} removeborder={removeborder}>
                    {items.map((item, index) => (
                        item.isSeparator ? (
                            <MenuSeparator key={index} theme={theme} />
                        ) : (
                            <MenuItem
                                key={index}
                                className={item.iconPosition === 'right' ? 'icon-right' : ''}
                                onClick={() => {
                                    item.onClick?.();
                                    setIsOpen(false);
                                }}
                                theme={theme}
                                menuFontWeight={menuFontWeight}
                                isSelected={item.isSelected}
                            >
                                {item.icon && <span>{item.icon}</span>}
                                {item.label}
                            </MenuItem>
                        )
                    ))}
                </Menu>
            )}
        </Wrapper>
    );
};

export default DropdownButton;
